import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { countryPropType } from 'helpers/customPropTypes';
import { toLowerCase } from 'helpers/utils';

import { usePageMessages } from 'hooks/selectors';
import { useMatchMedia } from 'hooks/useMatchMedia';
import promoBreakpoints from 'theme/promoBreakpoints';

import PromoCountryItem from './PromoCountryItem';
import PromoCurrencyList from './PromoCurrencyList';
import { CountryListDropdown, DropdownList, DropdownListHeader, DropdownListInner, DropdownNotFound, SearchInput, SearchInputWrapper } from './styles';
import { sendBrazeEvent } from '../../../analytics/braze';
import { BR_SEARCH_NO_COUNTRY_FROM, BR_SEARCH_NO_COUNTRY_TO } from '../../../constants/braze/brazeEvents';
import { BR_AMOUNT, BR_COUNTRY, BR_LANGUAGE, BR_WHAT_WAS_TYPED } from '../../../constants/braze/brazeParams';
import { LightDiskButton } from '../../no-auth/common/fromAuth/LightDiskButton/LightDiskButton';
import { PromoText } from '../../no-auth/common/promo-text/PromoText';

const PromoCountryList = ({ showCurrencies = true, ...props }) => {
    const isDesktop = useMatchMedia(promoBreakpoints.desktop1440);
    const DEFAULT_COUNTRY_HEIGHT = isDesktop ? 68 : 56;
    const NO_RESULT_HEIGHT = 70;
    const COUNT_ITEM_FOR_RESIZE = 5;
    const DEFAULT_SCROLL_HEIGHT = DEFAULT_COUNTRY_HEIGHT * COUNT_ITEM_FOR_RESIZE;

    const { gotIt, notificationText, favoriteCurrenciesCaption, allCurrenciesCaption, selectFromCountry, selectToCountry, searchNoResults, selectCurrency } =
        usePageMessages();
    const inputRef = useRef(null);

    const [countriesState, setCountriesState] = useState({ countryList: props.countryList, showFavorite: true, searchValue: '' });
    const [scrollHeight, setScrollHeight] = useState(DEFAULT_SCROLL_HEIGHT);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }

        return () => {
            sendEvents();
        };
    }, []);

    const sendEvents = () => {
        if (props.countryMenu && countriesState.countryList && countriesState.countryList.length === 0) {
            const { countryFrom, countryTo } = props.countryMenu;
            const lang = props.lang;
            const amountField = props.amountField;

            if (props.isFrom && amountField) {
                sendBrazeEvent(BR_SEARCH_NO_COUNTRY_FROM, {
                    [BR_WHAT_WAS_TYPED]: countriesState.searchValue,
                    [BR_COUNTRY]: countryFrom.code,
                    [BR_AMOUNT]: amountField.value,
                    [BR_LANGUAGE]: lang,
                });
            } else {
                sendBrazeEvent(BR_SEARCH_NO_COUNTRY_TO, {
                    [BR_WHAT_WAS_TYPED]: countriesState.searchValue,
                    [BR_COUNTRY]: countryTo.code,
                    [BR_AMOUNT]: amountField.value,
                    [BR_LANGUAGE]: lang,
                });
            }
        }
    };

    const favoriteCountry = (countries, prefix) => {
        return (
            <>
                <DropdownListHeader>{favoriteCurrenciesCaption}</DropdownListHeader>
                {countries
                    .filter((c) => c.favorite)
                    .map((c) => (
                        <PromoCountryItem
                            key={`f-${c.code}`}
                            country={c}
                            prefix={prefix}
                            gotIt={gotIt}
                            notificationText={notificationText}
                            showCurrencies={showCurrencies}
                            onClickHandler={props.handlerSelectCountry}
                            isLongPanel
                        />
                    ))}
                <DropdownListHeader>{allCurrenciesCaption}</DropdownListHeader>
            </>
        );
    };

    const search = (e) => {
        const term = e.target.value;
        const showFavorite = term === '' && !countriesState.showFavorite;
        const resultList = props.countryList.filter((country) => {
            const byNameOrSynonym =
                term === undefined || toLowerCase(country.name).indexOf(toLowerCase(term)) >= 0 || toLowerCase(country.synonym).indexOf(toLowerCase(term)) >= 0;

            const byCurrencyCode =
                Array.isArray(country.currencies) &&
                country.currencies.find((currency) => {
                    return toLowerCase(currency.code).indexOf(toLowerCase(term)) >= 0;
                });

            return byNameOrSynonym || byCurrencyCode;
        });

        const getScrollHeight = () => {
            if (resultList.length <= COUNT_ITEM_FOR_RESIZE && resultList.length !== 0) {
                return DEFAULT_COUNTRY_HEIGHT * resultList.length;
            } else {
                return resultList.length === 0 ? NO_RESULT_HEIGHT : DEFAULT_SCROLL_HEIGHT;
            }
        };

        setScrollHeight(getScrollHeight);

        setCountriesState({
            countryList: resultList,
            showFavorite: showFavorite,
            searchValue: term,
        });
    };

    const hasFavorite = countriesState.countryList.find((country) => country.favorite);
    const isFrom = props.isFrom;
    const prefix = isFrom ? 'from' : 'to';
    const showCountryList = props.showCountryList;
    const selectedCountry = props.selectedCountry;
    const searchNoResultsFound = Array.isArray(countriesState.countryList) && !countriesState.countryList.length;

    return (
        <CountryListDropdown>
            <SearchInputWrapper htmlFor={`field_${prefix}`}>
                {showCountryList ? (
                    <SearchInput
                        placeholder={isFrom ? selectFromCountry : selectToCountry}
                        autoComplete="off"
                        tabIndex={'0'}
                        type="search"
                        ref={inputRef}
                        id={`field_${prefix}`}
                        onChange={search}
                    />
                ) : (
                    <>
                        <SearchInput readOnly={true} defaultValue={selectedCountry.name} type="search" />
                        <LightDiskButton
                            onClick={(e) => props.handlerCloseButtonCurrencyCountry(e, selectedCountry)}
                            iconName="cross"
                            type="button"
                            raised={false}
                            marginRight="8px"
                            tabIndex={'0'}
                        />
                    </>
                )}
            </SearchInputWrapper>

            {showCountryList ? (
                <DropdownList scrollHeight={scrollHeight} tabIndex="0">
                    <DropdownListInner>
                        {hasFavorite && countriesState.showFavorite && favoriteCountry(countriesState.countryList, prefix)}
                        {countriesState.countryList.map((country) => (
                            <PromoCountryItem
                                key={country.code}
                                country={country}
                                prefix={prefix}
                                gotIt={gotIt}
                                notificationText={notificationText}
                                showCurrencies={showCurrencies}
                                onClickHandler={props.handlerSelectCountry}
                                isLongPanel
                                query={countriesState.searchValue}
                            />
                        ))}
                        {searchNoResultsFound && (
                            <DropdownNotFound>
                                <PromoText tag="div" variant="subheader" color="gray40" ellipsis>
                                    {searchNoResults}
                                </PromoText>
                            </DropdownNotFound>
                        )}
                    </DropdownListInner>
                </DropdownList>
            ) : (
                <PromoCurrencyList header={selectCurrency} country={selectedCountry} onClickHandler={props.handlerSelectCountry} />
            )}
        </CountryListDropdown>
    );
};

PromoCountryList.propTypes = {
    isFrom: PropTypes.bool.isRequired,
    countryList: PropTypes.arrayOf(countryPropType),
    handlerSelectCountry: PropTypes.func.isRequired,
};
const mapStateToProps = ({ language, calculator }) => ({
    lang: language.lang,
    countryMenu: calculator.countryMenu,
});

export default connect(mapStateToProps)(PromoCountryList);
